// src/pages/Register.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { useLocation } from "react-router-dom";
import { useConfig } from "../context/ConfigContext";
import BusinessUserPayment from "../components/BusinessUserPayment";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

const Register = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [company, setCompany] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const prefilledIdentifier = params.get("identifier") || "";
  const [step, setStep] = useState(1);
  const [identifier, setIdentifier] = useState(prefilledIdentifier);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const { config } = useConfig();
  const [userType, setUserType] = useState("regular");
  const [paymentMade, setPaymentMade] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  // Stripe state - updated
  const [stripePromise, setStripePromise] = useState(null);
  const [isStripeLoading, setIsStripeLoading] = useState(true);
  const [stripeError, setStripeError] = useState(null); // Added for error handling
  const [paymentIntentId, setPaymentIntentId] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(false);

  // Fetch the Stripe public key from the backend on mount
  useEffect(() => {
    const fetchStripeKey = async () => {
      try {
        setIsStripeLoading(true);
        const { data } = await axios.get("/api/config/stripe");
        console.log("Stripe key response:", data);

        if (data.publicKey) {
          // Create the promise with the public key
          const promise = await loadStripe(data.publicKey);
          setStripePromise(promise);
        } else {
          throw new Error("No public key in response");
        }
      } catch (err) {
        console.error("Error fetching Stripe public key:", err);
        setStripeError(
          "Could not load payment system. Please try again later."
        );
      } finally {
        setIsStripeLoading(false);
      }
    };

    fetchStripeKey();
  }, []);

  const checkIdentifierExists = async (type, value) => {
    try {
      setError("");

      // Skip empty values
      if (!value.trim()) return false;

      const { data } = await axios.post("/api/check-identifier", {
        identifier: value.trim(),
      });

      if (data.exists) {
        if (type === "email") {
          setError(
            "This email address is already registered. Please use a different email or sign in."
          );
        } else if (type === "phone") {
          setError(
            "This phone number is already registered. Please use a different number or sign in."
          );
        }
        return true;
      }

      return false;
    } catch (err) {
      console.error("Error checking identifier:", err);
      return false;
    }
  };

  const sanitizeInput = (input) => {
    return input.trim().replace(/\s+/g, " ");
  };

  const validateInputs = () => {
    const cleanEmail = sanitizeInput(email);
    const cleanPhone = sanitizeInput(phone);

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(cleanEmail)) {
      setError("Please enter a valid email address");
      return false;
    }

    // Phone validation
    const phoneRegex = /^\+?[\d\s-]{8,}$/;
    if (!phoneRegex.test(cleanPhone)) {
      setError("Please enter a valid phone number");
      return false;
    }

    // Password validation
    if (password.length < 6 || password.length > 12) {
      setError(
        "Password must be at least 8 characters long and less than 12 chracters"
      );
      return false;
    }

    // const passwordStrengthRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    // if (!passwordStrengthRegex.test(password)) {
    //   setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and special character');
    //   return false;
    // }

    // Postal code validation only if provided
    if (postalCode.trim()) {
      const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
      if (!postalCodeRegex.test(postalCode.trim())) {
        setError("Please enter a valid Canadian postal code");
        return false;
      }
    }

    // Required fields check
    if (
      !firstName.trim() ||
      !lastName.trim() ||
      !email.trim() ||
      !phone.trim()
    ) {
      setError("Please fill in all required fields");
      return false;
    }

    // Admin code validation (if provided)
    if (adminCode) {
      const cleanAdminCode = sanitizeInput(adminCode);
      if (cleanAdminCode.length < 6) {
        setError("Invalid admin code");
        return false;
      }
    }

    return true;
  };

  // function to handle payment completion
  // function to handle payment completion
  const handlePaymentComplete = (
    paymentId,
    paymentMethod,
    shouldSavePayment = true
  ) => {
    setPaymentMade(true);
    setPaymentIntentId(paymentId);
    setPaymentMethodId(paymentMethod);
    setShowPaymentForm(false);
    console.log("Payment completed:", {
      paymentId,
      paymentMethod,
      shouldSavePayment,
    });
  };

  // const handleSubmit = async (e) => {
  //   e.preventDefault();
  //   setError("");
  //   // If business user needs to make payment but hasn't yet
  //   if (
  //     userType === "business" &&
  //     config.businessUserCharging &&
  //     !paymentMade
  //   ) {
  //     setShowPaymentForm(true);
  //     return;
  //   }

  //   if (step !== 4) {
  //     return; // This is an additional safety check
  //   }

  //   if (step === 4 && !acceptedTerms) {
  //     setError("You must agree to the Terms & Conditions and Privacy Policy");
  //     return;
  //   }

  //   if (!validateInputs()) {
  //     return;
  //   }

  //   console.log("Form submitted - starting validation");
  //   if (step === 3 && postalCode.trim()) {
  //     const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
  //     if (!postalCodeRegex.test(postalCode.trim())) {
  //       setError("Please enter a valid Canadian postal code");
  //       return;
  //     }
  //   }

  //   try {
  //     const cleanEmail = sanitizeInput(email);
  //     const cleanPhone = sanitizeInput(phone);
  //     const cleanAdminCode = adminCode ? sanitizeInput(adminCode) : "";

  //     const requestData = {
  //       email: cleanEmail,
  //       password,
  //       phone: cleanPhone,
  //       firstName: firstName.trim(),
  //       lastName: lastName.trim(),
  //       adminCode: cleanAdminCode,
  //       company: company.trim(),
  //       userType: userType,
  //       businessVerificationPaymentId:
  //         userType === "business" && paymentMade ? paymentIntentId : undefined,
  //       savePaymentMethod:
  //         userType === "business" && paymentMade ? true : undefined, // ADD THIS LINE
  //       paymentMethodId:
  //         userType === "business" && paymentMade ? paymentMethodId : undefined, // ADD THIS LINE
  //     };

  //     // Only include addresses array if any address field is filled
  //     if (street.trim() || city.trim() || state.trim() || postalCode.trim()) {
  //       const addressData = {
  //         street: street.trim(),
  //         city: city.trim(),
  //         state: state.trim(),
  //         postalCode: postalCode.trim(),
  //         country: "Canada",
  //       };
  //       requestData.addresses = [addressData];
  //       console.log("Adding address to request:", addressData);
  //     }

  //     console.log("Final request data:", requestData);

  //     const { data } = await axios.post("/api/register", requestData);
  //     login(data.user, data.token);
  //     navigate("/");
  //   } catch (error) {
  //     const errorMessage =
  //       error.response?.data?.error || "Registration failed. Please try again.";
  //     setError(errorMessage);

  //     if (errorMessage.toLowerCase().includes("email")) {
  //       setStep(2); // Move back to first step where email field is

  //       // Add a small delay to ensure the DOM updates before trying to focus
  //       setTimeout(() => {
  //         const emailInput = document.querySelector('input[type="email"]');
  //         if (emailInput) {
  //           emailInput.focus();
  //         }
  //       }, 100);
  //     }

  //     // If we had a payment for business account but registration failed, refund it
  //     if (userType === "business" && paymentMade && paymentIntentId) {
  //       try {
  //         await axios.post("/api/refund-verification-payment", {
  //           paymentIntentId: paymentIntentId,
  //           reason: "Registration failed",
  //         });
  //         console.log(
  //           "Verification charge refunded due to failed registration"
  //         );
  //       } catch (refundError) {
  //         console.error("Failed to refund verification charge:", refundError);
  //       }
  //     }
  //   }
  // };

  // REPLACE the handleSubmit function in Register.js with this FIXED version:

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // If business user needs to make payment but hasn't yet
    if (
      userType === "business" &&
      config.businessUserCharging &&
      !paymentMade
    ) {
      setShowPaymentForm(true);
      return;
    }

    if (step !== 4) {
      return;
    }

    if (step === 4 && !acceptedTerms) {
      setError("You must agree to the Terms & Conditions and Privacy Policy");
      return;
    }

    if (!validateInputs()) {
      return;
    }

    console.log("Form submitted - starting validation");

    try {
      const cleanEmail = sanitizeInput(email);
      const cleanPhone = sanitizeInput(phone);
      const cleanAdminCode = adminCode ? sanitizeInput(adminCode) : "";

      const requestData = {
        email: cleanEmail,
        password,
        phone: cleanPhone,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        adminCode: cleanAdminCode,
        company: company.trim(),
        userType: userType,
        businessVerificationPaymentId:
          userType === "business" && paymentMade ? paymentIntentId : undefined,
        savePaymentMethod:
          userType === "business" && paymentMade ? true : undefined,
        paymentMethodId:
          userType === "business" && paymentMade ? paymentMethodId : undefined,
      };

      // Only include addresses array if any address field is filled
      if (street.trim() || city.trim() || state.trim() || postalCode.trim()) {
        const addressData = {
          street: street.trim(),
          city: city.trim(),
          state: state.trim(),
          postalCode: postalCode.trim(),
          country: "Canada",
        };
        requestData.addresses = [addressData];
        console.log("Adding address to request:", addressData);
      }

      console.log("Final request data:", requestData);

      // Make the registration request
      const { data } = await axios.post("/api/register", requestData);

      console.log("✅ Registration successful:", data.user.email);

      // Login the user and redirect
      login(data.user, data.token);
      navigate("/");
    } catch (error) {
      console.error("❌ Registration failed:", error);

      const errorMessage =
        error.response?.data?.error || "Registration failed. Please try again.";

      setError(errorMessage);

      // Reset payment state if registration failed
      if (userType === "business" && paymentMade) {
        setPaymentMade(false);
        setPaymentIntentId(null);
        setPaymentMethodId(null);
        console.log("Reset payment state due to registration failure");
      }

      // Move to appropriate step based on error
      if (errorMessage.toLowerCase().includes("email")) {
        setStep(2);
        setTimeout(() => {
          const emailInput = document.querySelector('input[type="email"]');
          if (emailInput) emailInput.focus();
        }, 100);
      } else if (errorMessage.toLowerCase().includes("phone")) {
        setStep(1);
        setTimeout(() => {
          const phoneInput = document.querySelector('input[type="tel"]');
          if (phoneInput) phoneInput.focus();
        }, 100);
      }
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-8 pt-24 pb-24">
      <div
        className="bg-white rounded-lg shadow-md p-8"
        style={{ marginTop: 50, marginBottom: 30 }}
      >
        <h2
          className="text-2xl font-bold mb-6 text-center"
          style={{ color: config.colors.textPrimary }}
        >
          Create Account
        </h2>

        <div className="w-full mb-6">
          <div className="flex justify-between mb-2">
            <span
              className={`font-medium ${step >= 1 ? "" : "text-gray-400"}`}
              style={{ color: step >= 1 ? config.colors.primary : undefined }}
            >
              Verification
            </span>
            <span
              className={`font-medium ${step >= 2 ? "" : "text-gray-400"}`}
              style={{ color: step >= 2 ? config.colors.primary : undefined }}
            >
              Account
            </span>
            <span
              className={`font-medium ${step >= 3 ? "" : "text-gray-400"}`}
              style={{ color: step >= 3 ? config.colors.primary : undefined }}
            >
              Personal Info
            </span>
            <span
              className={`font-medium ${step >= 4 ? "" : "text-gray-400"}`}
              style={{ color: step >= 4 ? config.colors.primary : undefined }}
            >
              Address
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="h-2.5 rounded-full transition-all duration-300"
              style={{
                width: `${(step / 4) * 100}%`,
                backgroundColor: config.colors.primary,
              }}
            ></div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 rounded-md p-4 mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off">
          {step === 1 && (
            <div className="space-y-6">
              <h3
                className="text-lg font-medium mb-4"
                style={{ color: config.colors.textPrimary }}
              >
                Phone Verification
              </h3>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  style={{ borderColor: `${config.colors.textSecondary}40` }}
                  onFocus={(e) => {
                    e.target.style.borderColor = config.colors.primary;
                    e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                    e.target.style.outline = "none";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = `${config.colors.textSecondary}40`;
                    e.target.style.boxShadow = "none";
                  }}
                  required
                  maxLength={20}
                />
              </div>
              {otpSent && (
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Verification Code *
                  </label>
                  <input
                    type="text"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ borderColor: `${config.colors.textSecondary}40` }}
                    onFocus={(e) => {
                      e.target.style.borderColor = config.colors.primary;
                      e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                      e.target.style.outline = "none";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = `${config.colors.textSecondary}40`;
                      e.target.style.boxShadow = "none";
                    }}
                    required
                    maxLength={6}
                  />
                </div>
              )}
              <div className="mt-6 flex flex-col space-y-2">
                {!otpSent ? (
                  <button
                    type="button"
                    onClick={async () => {
                      setError("");
                      // Validate phone format first
                      const phoneRegex = /^\+?[\d\s-]{8,}$/;
                      if (!phoneRegex.test(phone)) {
                        setError("Please enter a valid phone number");
                        return;
                      }

                      // Check if phone already exists
                      const phoneExists = await checkIdentifierExists(
                        "phone",
                        phone
                      );
                      if (phoneExists) {
                        return;
                      }
                      try {
                        await axios.post("/api/verify-phone", { phone });
                        setOtpSent(true);
                      } catch (err) {
                        setError(
                          err.response?.data?.error || "Failed to send OTP"
                        );
                      }
                    }}
                    className="w-full text-white py-3 px-6 rounded-md font-medium transition"
                    style={{
                      backgroundColor: config.colors.primary,
                      ":hover": {
                        backgroundColor: `${config.colors.primary}DD`,
                      },
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor =
                        config.colors.primary;
                    }}
                  >
                    Send Verification Code
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={async () => {
                        setError("");
                        try {
                          await axios.post("/api/verify-phone/confirm", {
                            phone,
                            verificationCode: otpCode,
                          });
                          setPhoneVerified(true);
                          setStep(2);
                        } catch (err) {
                          setError(
                            err.response?.data?.error ||
                              "OTP verification failed"
                          );
                        }
                      }}
                      className="w-full bg-green-500 text-white py-3 px-6 rounded-md font-medium hover:bg-green-600 transition"
                    >
                      Verify Code
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setError("");
                        try {
                          await axios.post("/api/verify-phone", { phone });
                          setOtpCode(""); // reset code if desired
                        } catch (err) {
                          setError(
                            err.response?.data?.error || "Failed to resend OTP"
                          );
                        }
                      }}
                      className="w-full text-white py-3 px-6 rounded-md font-medium transition"
                      style={{
                        backgroundColor: config.colors.primary,
                        ":hover": {
                          backgroundColor: `${config.colors.primary}DD`,
                        },
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor =
                          config.colors.primary;
                      }}
                    >
                      Resend Code
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium mb-4">Account Information</h3>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  style={{ borderColor: `${config.colors.textSecondary}40` }}
                  onFocus={(e) => {
                    e.target.style.borderColor = config.colors.primary;
                    e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                    e.target.style.outline = "none";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = `${config.colors.textSecondary}40`;
                    e.target.style.boxShadow = "none";
                  }}
                  required
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ borderColor: `${config.colors.textSecondary}40` }}
                    onFocus={(e) => {
                      e.target.style.borderColor = config.colors.primary;
                      e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                      e.target.style.outline = "none";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = `${config.colors.textSecondary}40`;
                      e.target.style.boxShadow = "none";
                    }}
                    required
                    maxLength={128}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    style={{
                      boxShadow: "none",
                      border: "none",
                      background: "transparent",
                      transform: "none",
                      marginTop: -20 + "px",
                    }}
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  At least 8 characters with uppercase, lowercase, number, and
                  special character.
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  User Type
                </label>
                <div className="space-y-2">
                  <label
                    className="flex items-center p-3 border rounded-md cursor-pointer transition-colors"
                    style={{
                      borderColor:
                        userType === "regular"
                          ? config.colors.primary
                          : `${config.colors.textSecondary}40`,
                      backgroundColor:
                        userType === "regular"
                          ? `${config.colors.primary}10`
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name="userType"
                      value="regular"
                      checked={userType === "regular"}
                      onChange={() => setUserType("regular")}
                      className="mr-2"
                      style={{ accentColor: config.colors.primary }}
                    />
                    <div>
                      <span className="font-medium">Regular Customer</span>
                      <p className="text-sm text-gray-500 mt-1">
                        For personal use and shopping
                      </p>
                    </div>
                  </label>

                  <label
                    className="flex items-center p-3 border rounded-md cursor-pointer transition-colors"
                    style={{
                      borderColor:
                        userType === "business"
                          ? config.colors.primary
                          : `${config.colors.textSecondary}40`,
                      backgroundColor:
                        userType === "business"
                          ? `${config.colors.primary}10`
                          : "transparent",
                    }}
                  >
                    <input
                      type="radio"
                      name="userType"
                      value="business"
                      checked={userType === "business"}
                      onChange={() => setUserType("business")}
                      className="mr-2"
                      style={{ accentColor: config.colors.primary }}
                    />
                    <div>
                      <span className="font-medium">Business Customer</span>
                      <p className="text-sm text-gray-500 mt-1">
                        For business purchases and bulk orders
                      </p>
                    </div>
                  </label>
                </div>

                {userType === "business" && config.businessUserCharging && (
                  <div
                    className="mt-3 p-3 rounded-md border "
                    style={{
                      borderColor: config.colors.primary,
                      color: config.colors.primaryText,
                    }}
                  >
                    <p className="text-sm">
                      Business accounts require a verification charge of $
                      {config.businessUserChargingAmount.toFixed(2)}. This
                      amount will be refunded after account verification.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              {userType === "business" ? (
                <h3 className="text-lg font-medium mb-4">
                  Contact Person Information
                </h3>
              ) : (
                <h3 className="text-lg font-medium mb-4">
                  Personal Information
                </h3>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ borderColor: `${config.colors.textSecondary}40` }}
                    onFocus={(e) => {
                      e.target.style.borderColor = config.colors.primary;
                      e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                      e.target.style.outline = "none";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = `${config.colors.textSecondary}40`;
                      e.target.style.boxShadow = "none";
                    }}
                    required
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ borderColor: `${config.colors.textSecondary}40` }}
                    onFocus={(e) => {
                      e.target.style.borderColor = config.colors.primary;
                      e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                      e.target.style.outline = "none";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = `${config.colors.textSecondary}40`;
                      e.target.style.boxShadow = "none";
                    }}
                    required
                    maxLength={50}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Legal Company Name (Optional)
                </label>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  style={{ borderColor: `${config.colors.textSecondary}40` }}
                  onFocus={(e) => {
                    e.target.style.borderColor = config.colors.primary;
                    e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                    e.target.style.outline = "none";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = `${config.colors.textSecondary}40`;
                    e.target.style.boxShadow = "none";
                  }}
                  maxLength={100}
                />
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium mb-4">
                Shipping Address (Optional)
              </h3>
              <div>
                <AddressAutocomplete
                  index={0} // Since registration only has one address
                  address={{
                    street: street,
                    city: city,
                    state: state,
                    postalCode: postalCode,
                    country: "Canada",
                  }}
                  onAddressChange={(index, field, value) => {
                    // Update the corresponding state based on the field
                    switch (field) {
                      case "street":
                        setStreet(value);
                        break;
                      case "city":
                        setCity(value);
                        break;
                      case "state":
                        setState(value);
                        break;
                      case "postalCode":
                        setPostalCode(value);
                        break;
                      default:
                        break;
                    }
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ borderColor: `${config.colors.textSecondary}40` }}
                    onFocus={(e) => {
                      e.target.style.borderColor = config.colors.primary;
                      e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                      e.target.style.outline = "none";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = `${config.colors.textSecondary}40`;
                      e.target.style.boxShadow = "none";
                    }}
                    maxLength={50}
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-medium mb-2">
                    Province
                  </label>
                  <input
                    type="text"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    className="w-full px-3 py-2 border rounded-md"
                    style={{ borderColor: `${config.colors.textSecondary}40` }}
                    onFocus={(e) => {
                      e.target.style.borderColor = config.colors.primary;
                      e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                      e.target.style.outline = "none";
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = `${config.colors.textSecondary}40`;
                      e.target.style.boxShadow = "none";
                    }}
                    maxLength={50}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Postal Code
                </label>
                <input
                  type="text"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md"
                  style={{ borderColor: `${config.colors.textSecondary}40` }}
                  onFocus={(e) => {
                    e.target.style.borderColor = config.colors.primary;
                    e.target.style.boxShadow = `0 0 0 2px ${config.colors.primary}30`;
                    e.target.style.outline = "none";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = `${config.colors.textSecondary}40`;
                    e.target.style.boxShadow = "none";
                  }}
                  maxLength={7}
                  placeholder="A1A 1A1"
                />
              </div>
              <div className="flex items-center mt-6">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mr-2"
                />
                <label
                  htmlFor="terms"
                  className="text-sm text-gray-700"
                  style={{ color: config.colors.textPrimary }}
                >
                  I agree to the{" "}
                  <a
                    href="/terms-conditions"
                    style={{
                      color: config.colors.primary,
                      textDecoration: "underline",
                    }}
                  >
                    Terms & Conditions
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy-policy"
                    style={{
                      color: config.colors.primary,
                      textDecoration: "underline",
                    }}
                  >
                    Privacy Policy
                  </a>
                  .
                </label>
              </div>
            </div>
          )}

          {step > 1 && (
            <div className="mt-8 flex justify-between">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setStep(step - 1);
                }}
                className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-md font-medium transition-colors focus:outline-none"
                style={{
                  ":hover": { backgroundColor: "#e5e7eb" },
                  ":focus": {
                    outlineColor: config.colors.primary,
                    boxShadow: `0 0 0 2px ${config.colors.primary}30`,
                  },
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = "#e5e7eb";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = "#e9e9e9";
                }}
              >
                Back
              </button>

              {step < 4 ? (
                <button
                  type="button" // This explicitly prevents form submission
                  onClick={(e) => {
                    e.preventDefault(); // Add this to be doubly sure
                    // Validation for each step
                    setError("");
                    // (Optional) Add validation for steps 2 and 3 here
                    setStep(step + 1);

                    if (step === 2) {
                      // Validate email, password and phone
                      const cleanEmail = sanitizeInput(email);
                      const cleanPhone = sanitizeInput(phone);

                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (!emailRegex.test(cleanEmail)) {
                        setError("Please enter a valid email address");
                        return;
                      }

                      const emailExists = checkIdentifierExists(
                        "email",
                        cleanEmail
                      );
                      if (emailExists) {
                        return;
                      }

                      // const phoneRegex = /^\+?[\d\s-]{8,}$/;
                      // if (!phoneRegex.test(cleanPhone)) {
                      //   setError('Please enter a valid phone number');
                      //   return;
                      // }

                      if (password.length < 6) {
                        setError("Password must be at least 8 characters long");
                        return;
                      }

                      const passwordStrengthRegex =
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
                      // if (!passwordStrengthRegex.test(password)) {
                      //   setError('Password must contain at least one uppercase letter, one lowercase letter, one number, and special character');
                      //   return;
                      // }
                    } else if (step === 3) {
                      // Validate name fields
                      if (!firstName.trim() || !lastName.trim()) {
                        setError("Please fill in all required fields");
                        return;
                      }

                      // Admin code validation (if provided)
                      if (adminCode) {
                        const cleanAdminCode = sanitizeInput(adminCode);
                        if (cleanAdminCode.length < 6) {
                          setError("Invalid admin code");
                          return;
                        }
                      }
                    }

                    setStep(step + 1);
                  }}
                  className="w-full text-white py-3 px-6 rounded-md font-medium transition"
                  style={{
                    backgroundColor: config.colors.primary,
                    ":hover": { backgroundColor: `${config.colors.primary}DD` },
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      config.colors.primary;
                  }}
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit" // This one should remain as submit for the final step
                  className="w-full text-white py-3 px-6 rounded-md font-medium transition"
                  style={{
                    backgroundColor: config.colors.primary,
                    ":hover": { backgroundColor: `${config.colors.primary}DD` },
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = `${config.colors.primary}DD`;
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor =
                      config.colors.primary;
                  }}
                >
                  Create Account
                </button>
              )}
            </div>
          )}
        </form>
      </div>

      {showPaymentForm &&
        userType === "business" &&
        config.businessUserCharging && (
          <Elements stripe={stripePromise}>
            <BusinessUserPayment
              amount={config.businessUserChargingAmount}
              email={email}
              onPaymentComplete={handlePaymentComplete}
              config={config}
            />
          </Elements>
        )}
    </div>
  );
};

export default Register;
